/*
 * Copyright (C) GMS Inc - All Rights Reserved
 * This file is part of GMSCloud. - Proprietary and confidential
 * Unauthorized copying of this file, via any medium is strictly prohibited
 *
 * Created on   : 18-Jul-2026
 * @author      : Vincy R
 * Requirement# : 22300
 * Purpose      : Provider view — featured Global Default banner + grid of trade-default cards.
 */

import React, { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import AddIcon from '@mui/icons-material/Add';
import { alpha } from '@mui/material/styles';
import { Trade, GlobalDefaultTemplate, Scope } from '../types';
import { hasTradeTemplate, activeVersion } from '../model';
import { globalAdopterCount, providerAdopterCount, templateStatus, tradePalette, relativeWhen } from './libraryModel';
import StatusChip, { HasDraftBadge } from './StatusChip';
import TemplateMiniCard, { VersionPill, CountPill } from './TemplateMiniCard';
import AddProviderDefaultModal from '../modal/AddProviderDefaultModal';

interface Props {
  trades: Trade[]; globalDefault: GlobalDefaultTemplate; query: string;
  /** openDraft=true opens the trade's saved provider draft for editing; otherwise the published current. */
  onOpenScope: (scope: Scope, tenantId: string | null, tradeId: string | null, openDraft?: boolean) => void;
}

/** Provider view: featured Global Default banner + grid of trade-default template cards. No categories. */
export default function ProviderGridView({ trades, globalDefault, query, onOpenScope }: Props) {
  const q = query.trim().toLowerCase();
  // A trade with a provider-scope DRAFT but no published version yet (e.g. just started via the "+"
  // picker, before its first Publish) still needs to show up here as a Draft-only card — hasTradeTemplate
  // alone would hide it (it checks published versions), same as a trade with neither.
  const providerTrades = useMemo(() => trades.filter(t => hasTradeTemplate(t) || t.provider?.draft), [trades]);
  const shown = useMemo(
    () => providerTrades.filter(t => !q || t.name.toLowerCase().includes(q) || t.category.toLowerCase().includes(q)),
    [q, providerTrades],
  );
  // Trades with no dedicated provider template AND no in-progress draft — eligible to start one via
  // the "+" picker. Excluding draft-having trades keeps a trade from reappearing here as though its
  // saved draft doesn't exist.
  const eligibleTrades = useMemo(() => trades.filter(t => !hasTradeTemplate(t) && !t.provider?.draft), [trades]);
  const [addOpen, setAddOpen] = useState(false);
  const globalMatches = !q || 'global default template'.includes(q);
  const hasGlobal = globalDefault.versions.length > 0;
  const gAdopters = globalAdopterCount(trades);

  return (
    <Box>
      {globalMatches && (
        <Box onClick={() => onOpenScope('global', null, null)}
          sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2.5, borderRadius: 3.5, cursor: 'pointer', mb: 3,
            background: theme => `linear-gradient(100deg, ${alpha(theme.palette.warning.light, 0.33)}, ${theme.palette.background.paper} 78%)`,
            border: 1, borderColor: 'warning.main' }}>
          <Box sx={{ width: 46, height: 46, borderRadius: 3, bgcolor: 'warning.main', color: '#fff', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 20 }}>G</Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Typography fontWeight={700} fontSize={16}>Global Default Template</Typography>
              <Chip size="small" label={hasGlobal ? 'Shared fallback' : 'Not configured'}
                sx={{ bgcolor: 'background.paper', color: 'warning.main', border: 1, borderColor: 'warning.main', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }} />
            </Box>
            <Typography fontSize={12.5} color="text.secondary" sx={{ mt: 0.4 }}>
              Adopted by any trade that has no dedicated template yet · {gAdopters} tenant{gAdopters !== 1 ? 's' : ''} currently on it
            </Typography>
          </Box>
          <Button size="small" variant="contained" onClick={e => { e.stopPropagation(); onOpenScope('global', null, null); }}>Open</Button>
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
        <Typography fontWeight={700} fontSize={13}>Trade default templates</Typography>
        <Typography fontSize={12} color="text.disabled">{shown.length} · adopted automatically when a tenant opts into the trade</Typography>
        <Box sx={{ flex: 1 }} />
        <Tooltip title={eligibleTrades.length ? 'Add a provider default for a trade that has none yet' : 'Every trade already has a dedicated provider template'}>
          <span>
            <IconButton size="small" color="primary" disabled={!eligibleTrades.length} onClick={() => setAddOpen(true)}
              aria-label="Add provider default">
              <AddIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      {shown.length === 0 ? (
        <Box sx={{ p: 5, textAlign: 'center', color: 'text.disabled', border: 1, borderColor: 'divider', borderRadius: 2 }}>No trade templates match your search.</Box>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 1.5 }}>
          {shown.flatMap(trade => {
            const active = activeVersion(trade.provider?.versions ?? []);
            const draft = trade.provider?.draft;
            const [softBg, ink] = tradePalette(trade.id);
            const initial = trade.name[0];
            const adopterCount = providerAdopterCount(trade);
            const whenText = (w?: string | null) => (relativeWhen(w ?? '')
              ? <Typography component="span" sx={{ ml: 'auto', fontSize: 11, color: 'text.disabled', whiteSpace: 'nowrap', flexShrink: 0 }}>{relativeWhen(w ?? '')}</Typography>
              : null);
            const cards: React.ReactNode[] = [];

            // Published card — same as before, just moved into TemplateMiniCard.
            if (active) {
              cards.push(
                <TemplateMiniCard key={`${trade.id}:pub`} initial={initial} name={trade.name} category={trade.category}
                  softBg={softBg} ink={ink} onClick={() => onOpenScope('provider', null, trade.id, false)}>
                  <StatusChip status={templateStatus(active)} />
                  {active.version && <VersionPill v={active.version} />}
                  <CountPill n={(active.tasks || []).length} />
                  {whenText(active.when)}
                  <Typography component="span" sx={{ fontSize: 11, color: 'text.disabled', whiteSpace: 'nowrap' }}>
                    {`${adopterCount} ${adopterCount === 1 ? 'adopter' : 'adopters'}`}
                  </Typography>
                </TemplateMiniCard>,
              );
            }

            // Separate draft card — same pattern as the By-tenant view: a draft never merges into the
            // published card, it's its own card so opening it edits the draft, not the published version.
            if (draft) {
              cards.push(
                <TemplateMiniCard key={`${trade.id}:draft`} draft initial={initial} name={trade.name} category={trade.category}
                  softBg={softBg} ink={ink} onClick={() => onOpenScope('provider', null, trade.id, true)}>
                  <HasDraftBadge when={draft.when} />
                  <CountPill n={draft.taskCount} />
                  {whenText(draft.when)}
                </TemplateMiniCard>,
              );
            }
            return cards;
          })}
        </Box>
      )}
      <AddProviderDefaultModal open={addOpen} trades={eligibleTrades} onClose={() => setAddOpen(false)}
        onSelect={tradeId => { setAddOpen(false); onOpenScope('provider', null, tradeId); }} />
    </Box>
  );
}
